<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full gap-y-5">
          <TextInput
            v-model="rawCommentType.text"
            type="text"
            name="text"
            :error="errors?.text"
            :label="$t('driverCommentType.text')"
          />
          <Switch
            v-model="rawCommentType.deleted"
            :error="errors?.deleted"
            :label="$t('driverCommentType.deleted')"
          />
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {boolean, object, string} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import TextInput from '@components/utils/inputs/Input.vue';
import { syncRef } from '@/hooks/state';
import type { DriverCommentType } from '@/entities/Entities';
import Switch from '@utils/inputs/Switch.vue';

interface Props {
  open: boolean;
  commentType?: DriverCommentType;
}

const emit = defineEmits(['update:open', 'update:commentType', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalCommentType = useVModel(props, 'commentType', emit);

const isNew = computed(() => !props.commentType);
const tKey = computed(() => (isNew.value
  ? 'pages.settings.driverCommentTypes.detailModal.new'
  : 'pages.settings.driverCommentTypes.detailModal.edit'));

const { t } = useI18n();

const notify = useNotify();

const rawCommentType = syncRef(internalCommentType, (commentType) => ({
  ...commentType,
}));

const {
  data: resultCommentType,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    text: string().required(t('validation.notEmpty')),
    deleted: boolean().nullable(),
  }),
  rawCommentType,
);

function resetData() {
  rawCommentType.value = { ...internalCommentType.value };
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalCommentType.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.commentType;
    const url = edit ? props.commentType['@id'] : '/driver_comment_types';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        text: resultCommentType.value.text,
        deleted: resultCommentType.value.deleted ?? false,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
